import { yupAddress, yupBoolean, yupNumber, yupObject, yupPeriodicValue, yupString } from '../../yup';

export const employmentSchema = yupObject({
  type: yupString().required().label('Employment type'),
});

export const simpleSalarySchema = yupObject({
  salary: yupPeriodicValue(yupNumber().required()).label('Salary'),
});

export const newSalarySchema = yupObject({
  salary: yupPeriodicValue(yupNumber().required()).label('Salary'),
  commission: yupPeriodicValue().label('Commissions'),
  bonuses: yupPeriodicValue().label('Bonuses'),
  overtime: yupPeriodicValue().label('Overtime pay'),
  salarySacrifice: yupBoolean().required().label('Salary sacrifice'),
  employerName: yupString().required().label('Name of employer'),
  employerAddress: yupAddress().required().label('Employer address'),
  employedSince: yupString().required().label('Date commenced'),
});

export const updateSalarySchema = yupObject({
  salary: yupPeriodicValue(yupNumber().required()).label('Salary'),
  commission: yupPeriodicValue().label('Commissions'),
  bonuses: yupPeriodicValue().label('Bonuses'),
  overtime: yupPeriodicValue().label('Overtime pay'),
  salarySacrifice: yupBoolean().required().label('Salary sacrifice'),
  employerName: yupString().required().label('Name of employer'),
  employerAddress: yupAddress().required().label('Employer address'),
  employedSince: yupString().required().label('Date commenced'),
});

// Broker-specific salary schemas (without salarySacrifice field)
const brokerSalaryFields = {
  salary: yupPeriodicValue(yupNumber().required()).label('Salary'),
  commission: yupPeriodicValue().label('Commissions'),
  bonuses: yupPeriodicValue().label('Bonuses'),
  overtime: yupPeriodicValue().label('Overtime pay'),
  employerName: yupString().required().label('Name of employer'),
  employerAddress: yupAddress().required().label('Employer address'),
  employedSince: yupString().required().label('Date commenced'),
};

export const brokerNewSalarySchema = yupObject(brokerSalaryFields);
export const brokerUpdateSalarySchema = yupObject(brokerSalaryFields);

// Occupation/industry fields shared by the ERHL salary schemas (optional)
const occupationIndustryFields = {
  occupation: yupString()
    .test('len', 'Max characters input is 120', (val) => !val || val.length <= 120)
    .label('Occupation'),
  industry: yupString()
    .test('len', 'Max characters input is 255', (val) => !val || val.length <= 255)
    .label('Industry'),
};

const erhlSalaryFields = {
  ...brokerSalaryFields,
  ...occupationIndustryFields,
};

export const erhlNewSalarySchema = yupObject(erhlSalaryFields);
export const erhlUpdateSalarySchema = yupObject(erhlSalaryFields);
export const erhlRequestNewSalarySchema = newSalarySchema.concat(yupObject(occupationIndustryFields));
export const erhlRequestUpdateSalarySchema = updateSalarySchema.concat(yupObject(occupationIndustryFields));

export const selfEmployedSchema = yupObject({
  salary: yupPeriodicValue().required().label('Salary'),
  businessAddress: yupAddress().required().label('Business address'),
  businessType: yupString()
    .required()
    .test('len', 'Max characters input is 255', (val) => val.length <= 255)
    .label('Business type'),
  tradingName: yupString()
    .required()
    .test('len', 'Max characters input is 255', (val) => val.length <= 255)
    .label('Trading name'),
  occupation: yupString()
    .test('len', 'Max characters input is 120', (val) => !val || val.length <= 120)
    .label('Occupation'),
  industry: yupString()
    .test('len', 'Max characters input is 255', (val) => !val || val.length <= 255)
    .label('Industry'),
  gstRegistered: yupBoolean().required('You must select an option'),
  abn: yupString()
    .matches(/^[0-9]*$/, 'Numbers only')
    .length(11)
    .required()
    .label('ABN'),
  soleTrader: yupBoolean().required('You must select an option'),
  providedDocument: yupString().required('You must select an option'),
  otherProvidedDocument: yupString()
    .when('providedDocument', {
      is: 'other',
      then: (schema) => schema.required('You must provide a document if "Other" is selected'),
      otherwise: (schema) => schema.notRequired(),
    })
    .label('Other document'),
  employedSince: yupString().required().label('Date commenced'),
});

const additionalIncomeAmountFields = {
  centrelinkPayments: yupPeriodicValue().label('Centrelink payments'),
  childSupportMaintenance: yupPeriodicValue().label('Child support maintenance'),
  familyAllowance: yupPeriodicValue().label('Family allowance'),
  familyTaxBenefit: yupPeriodicValue().label('Family tax benefit'),
  governmentPension: yupPeriodicValue().label('Government pension'),
  dividendsInvestments: yupPeriodicValue().label('Dividends and investments'),
  superannuation: yupPeriodicValue().label('Superannuation'),
  other: yupPeriodicValue().label('Other'),
  expectIncomeChange: yupBoolean().required().label('Income expectation'),
  effectOnIncomeChange: yupString()
    .when('expectIncomeChange', {
      is: true,
      then: (schema) => schema.required(),
    })
    .label('Expected income change'),
};

export const additionalIncomeSchema = yupObject({
  shortTermChange: yupBoolean().required().label('This question'),
  shortTermEmploymentChange: yupString()
    .when('shortTermChange', {
      is: true,
      then: (schema) => schema.required(),
    })
    .label('Future employment details'),
  additionalIncome: yupBoolean().required().label('This question'),
  ...additionalIncomeAmountFields,
});

export const erhlRequestAdditionalIncomeSchema = (isRetired: boolean) =>
  yupObject({
    shortTermChange: isRetired ? yupBoolean().optional() : yupBoolean().required().label('This question'),
    shortTermEmploymentChange: yupString()
      .when('shortTermChange', {
        is: (val: boolean) => !isRetired && val === true,
        then: (schema) => schema.required(),
      })
      .label('Future employment details'),
    additionalIncome: isRetired ? yupBoolean().optional() : yupBoolean().required().label('This question'),
    ...additionalIncomeAmountFields,
  });

/**
 * Schema for Company/Trust income (Net Profit)
 * Used for corporate borrowers with ABN and trading status
 */
export const companyIncomeSchema = yupObject({
  netProfit: yupPeriodicValue(yupNumber().required().min(1, 'Net profit amount is required')).label(
    'Net profit amount',
  ),
});
